import { Button, CircularProgress, Typography } from "@mui/material";
import { AppModal, bodyTextSx } from "global/components/AppModal";

interface ConfirmationModalProps {
  confirmationMessage: string;
  confirmationTitle: string;
  confirmationFooterButtonName?: string;
  closeButtonName?: string;
  leaveButtonName?: string;
  handleButtonCall?: () => Promise<void> | void;
  open: boolean;
  onClose: () => void;
  /** Spinner in the confirm button while the caller's work is in flight. */
  isSubmitting?: boolean;
  onLeave?: () => void;
}

/**
 * "You have unsaved work" prompt, with an optional third way out (`onLeave`) for
 * the flows that let you abandon the draft as well as save it or stay.
 *
 * This is not a modal of its own — it is `AppModal` with that three-button
 * footer pre-wired, kept as a named component because the same prompt is raised
 * from more than one editor.
 */
export default function CustomConfirmationModal({
  confirmationMessage,
  confirmationTitle,
  confirmationFooterButtonName = "Save",
  closeButtonName = "Stay on Page",
  leaveButtonName = "Leave",
  handleButtonCall,
  open,
  onClose,
  isSubmitting = false,
  onLeave,
}: ConfirmationModalProps) {
  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={confirmationTitle}
      tone="neutral"
      size="sm"
      busy={isSubmitting}
      footer={
        <>
          <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
            {closeButtonName}
          </Button>
          {onLeave && (
            <Button
              variant="outlined"
              onClick={onLeave}
              disabled={isSubmitting}
            >
              {leaveButtonName}
            </Button>
          )}
          {handleButtonCall && (
            <Button
              variant="contained"
              onClick={handleButtonCall}
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={15} thickness={5} color="inherit" />
                ) : undefined
              }
            >
              {confirmationFooterButtonName}
            </Button>
          )}
        </>
      }
    >
      <Typography sx={bodyTextSx}>{confirmationMessage}</Typography>
    </AppModal>
  );
}
